'use client';

import { useContext, useState, FC, ReactNode } from 'react';
import {
  Button,
  ButtonGroup,
  CellCheckbox,
  CellActions,
  CellContainer,
  CellUserName,
  Checkbox,
  Dialog,
  Dropdown,
  Icon,
  Pill,
  ResultListItem,
  Text,
  Timestamp,
  Toggle,
  Tooltip,
} from '@/_components';
import {
  getTaxReturnStatus,
  getAggregateVerificationStatus,
} from '@/_utils/taxReturns';
import { TaxReturnCellProps } from './TaxReturnCell.types';
import { SignatureCountCell } from './components/SignatureCountCell';
import { PaymentsCountCell } from './components/PaymentsCountCell';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { getClasses } from '@/_utils/classnames';
import { IdentityVerificationPill } from '@/_components/IdentityVerificationPill';
import { AdminTaxReturnsContext } from '@/_contexts/admin/AdminTaxReturnsProvider';
import { updateInvoicePaidStatus } from '@/_api/admin-tax-returns';
import { UserContext } from '@/_contexts/UserProvider';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { K1PackageStatusCell } from './components/K1PackageStatusCell';
import { useRouter } from 'next/navigation';
import { TaxReturn } from '@/_types/models/taxReturns';
import { REMINDER_DISABLED_REASONS } from '@/_constants/tax-return';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';

export const TaxReturnCell: FC<TaxReturnCellProps> = ({
  deleteTaxReturn,
  hasExtraPadding,
  isInline,
  isLastColumn,
  isSelected,
  onOpenVerification,
  sendReminders,
  showTaxReturn,
  taxReturn,
  toggleTaxReturnSelection,
  type,
  updateTaxReturn,
}) => {
  const { addToast } = useContext(ToastContext);
  const { refreshCompanyTaxReturns, selectedCompany } =
    useContext(CompanyContext);
  const { firmUsers, preferences } = useContext(FirmSettingsContext);
  const { token, permissions } = useContext(UserContext);
  const { push } = useRouter();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { icon, text, intent, value } = getTaxReturnStatus(taxReturn);

  const isActionRequired: boolean = value === 'action_required';
  const isFinished: boolean = value === 'finished';
  const isFiled: boolean = value === 'filed';
  const hasVouchers: boolean = !!taxReturn.payment_vouchers?.length;
  const hasInvoicePayment: boolean = !!taxReturn.payment;
  const isInvoicePaid: boolean = taxReturn.payment?.status === 'paid';
  const hasIdentityVerification: boolean =
    !!preferences.tax_return_identity_verification_enabled &&
    !!taxReturn.tax_return_identity_verifications?.length;

  // Treat as requiring signatures only if a form has actual signature entries
  // or explicitly requires wet signatures. Empty signature arrays do not count.
  const hasESignatures: boolean = !!taxReturn.e_sign_forms?.some((form) => {
    const hasESignEntries =
      Array.isArray((form as any).signatures) &&
      (form as any).signatures.length > 0;
    const requiresWetSignatures = (form as any).wetSignaturesRequired === true;
    return hasESignEntries || requiresWetSignatures;
  });

  // Consider signatures complete if API marks all_signed OR there are no forms requiring signatures.
  // Consider payments pending only when vouchers exist and not all are paid.
  const allSignedAndPaid: boolean =
    (taxReturn.all_signed || !hasESignatures) &&
    !(hasVouchers && !taxReturn.all_paid);

  const allDone: boolean =
    (isFiled || isFinished) &&
    !hasVouchers &&
    !hasInvoicePayment &&
    !hasESignatures;

  const reminderToggleDisabled: boolean =
    allSignedAndPaid || isActionRequired || allDone;

  const reminderDisabledReason: string = allSignedAndPaid
    ? REMINDER_DISABLED_REASONS?.all_signatures_signed_and_all_vouchers_paid
    : isActionRequired
      ? REMINDER_DISABLED_REASONS?.action_required
      : allDone
        ? REMINDER_DISABLED_REASONS?.all_signatures_signed_and_all_vouchers_paid
        : '';

  const {
    setSelectedTaxReturnForActivity,
    setSelectedTaxReturnExport,
    setSelectedPaymentVouchersReturn,
    setSelectedSignaturesReturn,
    setSelectedReminderReturn,
    downloadTaxReturn,
    getTaxReturns,
    setSelectedIdentityVerificationReturn,
  } = useContext(AdminTaxReturnsContext);

  const handleTaxReturnDeletion = async () => {
    if (!taxReturn || !deleteTaxReturn) {
      return;
    }

    try {
      setIsLoading(true);
      await deleteTaxReturn(taxReturn.id, taxReturn.company_id);
      handleActionAfterCall('Tax return has been deleted');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsDeleteDialogOpen(false);
      setIsLoading(false);
    }
  };

  const handleMarkAsFinished = async () => {
    if (!taxReturn || !updateTaxReturn) {
      return;
    }

    try {
      setIsLoading(true);
      await updateTaxReturn(taxReturn.id, taxReturn.company_id, {
        status: 'finished',
      });
      handleActionAfterCall('Tax return has been marked as finished');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReminderUpdate = async () => {
    if (!taxReturn || !updateTaxReturn) {
      return;
    }

    try {
      setIsLoading(true);
      await updateTaxReturn(taxReturn.id, taxReturn.company_id, {
        reminder_disabled: !taxReturn.reminder_disabled,
      });
      handleActionAfterCall('Reminder disabled updated');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVoucherStatusUpdate = async () => {
    if (!taxReturn || !updateTaxReturn) {
      return;
    }

    setSelectedPaymentVouchersReturn(taxReturn);
  };

  const handleAllSignaturesCompleted = async () => {
    if (!taxReturn) {
      return;
    }

    setSelectedSignaturesReturn(taxReturn);
  };

  const handleIdentityVerificationUpdate = async () => {
    if (!taxReturn) {
      return;
    }

    setSelectedIdentityVerificationReturn(taxReturn);
  };
  const handleStripePaymentUpdate = async () => {
    if (!taxReturn) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await updateInvoicePaidStatus({
        id: taxReturn.id,
        company_id: taxReturn.company_id,
        paid: taxReturn.payment?.status !== 'paid',
        token,
      });
      if (!response.error) {
        handleActionAfterCall('Invoice paid status updated');
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const hasSendTaxReturnPermission: boolean = getHasPermission(
    PermissionsMapEnum['tax_return_delivery.send'],
    permissions,
  );

  const hasEditTaxReturnPermission: boolean = getHasPermission(
    PermissionsMapEnum['tax_return_delivery.edit'],
    permissions,
  );

  const hasDeleteTaxReturnPermission: boolean = getHasPermission(
    PermissionsMapEnum['tax_return_delivery.delete'],
    permissions,
  );

  const openK1DistributionDialog = (taxReturn: TaxReturn) => {
    push(
      `/admin/${taxReturn.company_hash}/tax-returns/${taxReturn.id}/k1-distribution`,
    );
  };

  const handleActionAfterCall = (message: string) => {
    if (!!selectedCompany) {
      refreshCompanyTaxReturns();
    } else {
      getTaxReturns();
    }
    addToast('success', message);
  };

  const getCellContent = () => {
    const aggregateStatus = getAggregateVerificationStatus(
      taxReturn.stripe_identity_verifications,
      taxReturn.signers,
    );

    const showDistributeK1Button =
      taxReturn.k1_distribution_status !== 'not_applicable' &&
      !['processing', 'action_required'].includes(taxReturn.status);

    const actions: Array<ResultListItem> = [
      ...(isFiled
        ? [
            {
              name: 'Unmark filed',
              icon: 'x' as const,
              onClick: async () => {
                try {
                  setIsLoading(true);
                  await updateTaxReturn?.(taxReturn.id, taxReturn.company_id, {
                    status: 'unmark_filed',
                  });
                  handleActionAfterCall(
                    'Tax return has been unmarked as filed',
                  );
                } catch (error) {
                  addToast('error', null, { error });
                } finally {
                  setIsLoading(false);
                }
              },
              isDisabled: !hasEditTaxReturnPermission,
            },
            {
              name: 'Mark as finished',
              icon: 'check' as const,
              onClick: handleMarkAsFinished,
              isDisabled: !hasEditTaxReturnPermission,
            },
          ]
        : !isFinished // to be deprecated
          ? [
              {
                name: 'Mark as finished',
                icon: 'check' as const,
                onClick: handleMarkAsFinished,
                isDisabled: !hasEditTaxReturnPermission,
              },
              {
                name: 'Mark filed',
                icon: 'file' as const,
                onClick: async () => {
                  try {
                    setIsLoading(true);
                    await updateTaxReturn?.(
                      taxReturn.id,
                      taxReturn.company_id,
                      {
                        status: 'filed',
                      },
                    );
                    handleActionAfterCall(
                      'Tax return has been marked as filed',
                    );
                  } catch (error) {
                    addToast('error', null, { error });
                  } finally {
                    setIsLoading(false);
                  }
                },
                isDisabled: !hasEditTaxReturnPermission,
              },
            ]
          : [
              {
                name: 'Unmark finished',
                icon: 'x' as const,
                onClick: async () => {
                  try {
                    setIsLoading(true);
                    await updateTaxReturn?.(
                      taxReturn.id,
                      taxReturn.company_id,
                      {
                        status: 'unmark_finished',
                      },
                    );
                    handleActionAfterCall(
                      'Tax return has been unmarked as finished',
                    );
                  } catch (error) {
                    addToast('error', null, { error });
                  } finally {
                    setIsLoading(false);
                  }
                },
                isDisabled: !hasEditTaxReturnPermission,
              },
            ]),
      ...(!isActionRequired && hasESignatures
        ? [
            {
              name: 'Update signature status',
              icon: 'signature' as const,
              onClick: handleAllSignaturesCompleted,
              isDisabled: !hasEditTaxReturnPermission,
            },
          ]
        : []),
      ...(!isActionRequired && hasInvoicePayment
        ? [
            {
              name: isInvoicePaid ? 'Mark invoice unpaid' : 'Mark invoice paid',
              icon: 'dollar' as const,
              onClick: handleStripePaymentUpdate,
              isDisabled: !hasEditTaxReturnPermission,
            },
          ]
        : []),
      ...(!isActionRequired && hasVouchers
        ? [
            {
              name: 'Update payment vouchers',
              icon: 'newspaper-clipping' as const,
              onClick: handleVoucherStatusUpdate,
              isDisabled: !hasEditTaxReturnPermission,
            },
          ]
        : []),
      ...(!isActionRequired && hasIdentityVerification
        ? [
            {
              name: 'Update identity verification',
              icon: 'hash' as const,
              onClick: handleIdentityVerificationUpdate,
              isDisabled: !hasEditTaxReturnPermission,
            },
          ]
        : []),
      { type: 'separator' as const },
      ...(!reminderToggleDisabled
        ? [
            {
              name: 'Reminders',
              icon: 'bell' as const,
              items: [
                {
                  type: 'category' as const,
                  name: 'Reminders',
                },
                {
                  name: 'Send reminder immediately',
                  onClick: () =>
                    sendReminders?.(taxReturn.id, taxReturn.company_id),

                  isDisabled: !hasEditTaxReturnPermission,
                },
                {
                  name: 'Edit reminder message',
                  onClick: () => {
                    setSelectedReminderReturn(taxReturn);
                  },
                  isDisabled: !hasEditTaxReturnPermission,
                },
              ],
            },
          ]
        : []),
      {
        name: 'Download',
        icon: 'download' as const,
        items: [
          {
            type: 'category' as const,
            name: 'Download',
          },
          {
            name: 'Tax return pages',
            onClick: () => downloadTaxReturn(taxReturn, false, false),
          },
          {
            name: 'Signature pages',
            onClick: () => downloadTaxReturn(taxReturn, true, true),
          },
          {
            name: 'All pages',
            onClick: () => downloadTaxReturn(taxReturn, true, false),
          },
        ],
      },
      ...(!isActionRequired
        ? [
            {
              name: 'Send to Documents',
              icon: 'send' as const,
              onClick: () => setSelectedTaxReturnExport(taxReturn),
              isDisabled: !hasSendTaxReturnPermission,
            },
          ]
        : []),
      ...(showDistributeK1Button &&
      preferences.tax_return_k1_distribution_enabled
        ? [
            {
              name: 'Distribute K-1 packages',
              icon: 'send' as const,
              onClick: () => openK1DistributionDialog(taxReturn),
              isDisabled: !hasSendTaxReturnPermission,
            },
          ]
        : []),
      ...(!isActionRequired
        ? [
            {
              name: 'View activity log',
              icon: 'pulse' as const,
              onClick: () => setSelectedTaxReturnForActivity(taxReturn),
            },
          ]
        : []),
      { type: 'separator' as const },
      {
        name: 'Delete',
        icon: 'trash' as const,
        onClick: () => setIsDeleteDialogOpen(true),
        isDisabled: !hasDeleteTaxReturnPermission,
      },
    ];

    const assignedAdvisor = firmUsers?.find(
      (fu) => fu.id === taxReturn.assigned_advisor_id,
    );

    const onSelectQuestionnaire = () => toggleTaxReturnSelection?.(taxReturn);

    const entityId: string | undefined = taxReturn?.external_entity_id;

    switch (type) {
      case 'select': {
        if (isInline) return null;

        return (
          <CellCheckbox>
            <Checkbox
              label="Select"
              isLabelHidden={true}
              isChecked={isSelected || false}
              onChange={onSelectQuestionnaire}
            />
          </CellCheckbox>
        );
      }
      case 'company_name':
        return (
          <CellUserName
            companyName={taxReturn.company_name}
            companyHash={taxReturn.company_hash}
          >
            {!!entityId && (
              <Pill
                intent="secondary"
                label={entityId}
                isDark={true}
                allowCopy={true}
              />
            )}
          </CellUserName>
        );
      case 'tax_year':
        return <CellContainer text={taxReturn.tax_year} width={64} />;

      case 'updated_at':
        return (
          <CellContainer width={140}>
            <Text type="tiny" color="secondary">
              {!!taxReturn?.updated_at ? (
                <Timestamp timestamp={taxReturn?.updated_at} />
              ) : (
                <Icon size={16} name="minus" color="disabled" />
              )}
            </Text>
          </CellContainer>
        );

      case 'last_reminder_sent_at':
        return (
          <CellContainer width={96}>
            <Text type="tiny" color="secondary">
              {!!taxReturn?.last_reminder_sent_at ? (
                <Timestamp timestamp={taxReturn?.last_reminder_sent_at} />
              ) : (
                <Icon size={16} name="minus" color="disabled" />
              )}
            </Text>
          </CellContainer>
        );

      case 'last_signed_at':
        return (
          <CellContainer width={120}>
            <Text type="tiny" color="secondary">
              {!!taxReturn?.last_signed_at ? (
                <Timestamp timestamp={taxReturn?.last_signed_at} />
              ) : (
                <Icon size={16} name="minus" color="disabled" />
              )}
            </Text>
          </CellContainer>
        );

      case 'tax_return_name':
        return (
          <CellContainer width={180}>
            <a href="#" onClick={() => showTaxReturn?.(taxReturn.id)}>
              <Text type="tiny" lineClamp={2} underline={true}>
                {taxReturn.tax_return_name}
              </Text>
            </a>
          </CellContainer>
        );

      case 'current_status':
        return (
          <CellContainer>
            <a href="#" onClick={() => showTaxReturn?.(taxReturn.id)}>
              <Pill iconLeft={icon} label={text} intent={intent} />
            </a>
          </CellContainer>
        );

      case 'signatures':
        return (
          <CellContainer>
            <SignatureCountCell taxReturn={taxReturn} />
          </CellContainer>
        );

      case 'payments':
        return (
          <CellContainer>
            <PaymentsCountCell taxReturn={taxReturn} />
          </CellContainer>
        );

      case 'assigned_advisor':
        return <CellContainer width={96} text={assignedAdvisor?.name} />;

      case 'reminder_disabled':
        return (
          <CellContainer width={64}>
            <Tooltip placement="bottom">
              <Tooltip.Trigger>
                <Toggle
                  isDisabled={reminderToggleDisabled}
                  isSmall={true}
                  isChecked={
                    !taxReturn.reminder_disabled && !reminderToggleDisabled
                  }
                  onChange={handleReminderUpdate}
                  isLabelHidden={true}
                  label="Reminder"
                />
              </Tooltip.Trigger>
              {reminderToggleDisabled && !!reminderDisabledReason && (
                <Tooltip.Content>
                  <Text type="small" color="currentcolor">
                    {reminderDisabledReason}
                  </Text>
                </Tooltip.Content>
              )}
            </Tooltip>
          </CellContainer>
        );
      case 'actions':
        return (
          <CellActions>
            <ButtonGroup>
              <Dropdown items={actions} placement="left">
                <Button
                  value="Actions"
                  size="small"
                  intent="ghost"
                  iconLeft="dots-three"
                  isIconOnly={true}
                />
              </Dropdown>
            </ButtonGroup>
          </CellActions>
        );
      case 'identity_verification_status':
        return (
          <CellContainer>
            <div
              onClick={() => onOpenVerification?.(taxReturn)}
              style={{ cursor: 'pointer' }}
            >
              <IdentityVerificationPill {...aggregateStatus} />
            </div>
          </CellContainer>
        );
      case 'k1_distribution_status':
        return (
          <CellContainer>
            <K1PackageStatusCell taxReturn={taxReturn} />
          </CellContainer>
        );
    }
  };

  const cellContent: ReactNode = getCellContent();

  const taxReturnCellClasses: string = getClasses({
    sorabanTable__fixedColumn: isLastColumn,
    extraPadding: hasExtraPadding,
  });

  return (
    <>
      <Dialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Are you sure you want to delete this tax return?"
      >
        <Dialog.Content>
          <ButtonGroup align="right">
            <Button
              intent="primary"
              isDisabled={isLoading}
              onClick={handleTaxReturnDeletion}
              value="Yes"
            />
            <Button
              intent="danger-secondary"
              isDisabled={isLoading}
              onClick={() => setIsDeleteDialogOpen(false)}
              value="No"
            />
          </ButtonGroup>
        </Dialog.Content>
      </Dialog>

      <td className={taxReturnCellClasses}>{cellContent}</td>
    </>
  );
};
